/* Shared cell/field helpers for this module's ProzoDataGrid tables and search/filter bars —
   mirrors the pattern used on Forward Shipments (src/app/pages/orders/UnprocessedOrdersForward.js)
   so RVP-QC's tables read the same as the rest of the admin panel. */
import React from 'react'
import { Box, Stack, Typography, InputAdornment, Autocomplete, TextField } from '@mui/material'
import { alpha } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'
import SearchIcon from '@mui/icons-material/Search'

// API values arrive as raw codes/enums — SNAKE_CASE, camelCase, kebab-case (e.g. CHECK_DAMAGED,
// imageUpload, REV_REG_QC). Showing them raw (and in a monospace font) reads off-standard next to
// the rest of the Poppins UI. humanize() renders them as clean Title Case while preserving genuine
// acronyms. The underlying value/id is never touched — this is display-only.
const ACRONYMS = new Set(['QC', 'RVP', 'RTO', 'SLA', 'API', 'ID', 'JSON', 'URL', 'CP', 'PCIS', 'D2C', 'B2B', 'B2C', 'NDR', 'NPR', 'SKU', 'AWB', 'OTP', 'PIN', 'GST', 'HSN', 'COD', 'EDD'])
export function humanize(v) {
  if (v == null) return ''
  const s = String(v).trim()
  if (!s) return ''
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase → camel Case
    .split(/[\s_\-.]+/).filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
}

// Many params carry a description that's just a copy of the code (e.g. code SIZE, description "Size";
// code DAMAGE_CHECK, description "Damage Check"). Showing both reads as a broken duplicate
// ("Size — Size"). A description is only worth showing when it says something the code doesn't.
const _norm = (x) => String(x || '').replace(/[^a-z0-9]/gi, '').toLowerCase()
export const hasMeaningfulDesc = (code, description) => !!(description && _norm(description) && _norm(description) !== _norm(code))

export const CellLine = ({ children, muted, bold, ellipsis }) => (
  <Box sx={{
    fontSize: 12.5, lineHeight: 1.4,
    color: muted ? 'text.secondary' : 'text.primary',
    fontWeight: bold ? 600 : 400,
    ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
  }}>
    {children}
  </Box>
)

export const CellStack = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>
    {children}
  </Box>
)

export const CellMono = ({ children, muted }) => (
  <Box component="span" sx={{
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12.5, fontWeight: 600, letterSpacing: '-0.01em',
    color: muted ? 'text.secondary' : 'text.primary',
  }}>
    {children}
  </Box>
)

// A QC-parameter/enum code rendered for humans: humanized Title-Case in the module's own font
// (not monospace), with the exact raw code kept on hover for admins who need the literal value.
export const CodeText = ({ code, muted, bold = true }) => (
  <Box component="span" title={code || ''} sx={{
    fontSize: 12.5, fontWeight: bold ? 600 : 400, lineHeight: 1.4,
    color: muted ? 'text.secondary' : 'text.primary',
  }}>
    {humanize(code)}
  </Box>
)

// Standard two-line code cell used across this module's grids: humanized code over its
// plain-language description (if any).
export const CodeCell = ({ code, description }) => (
  <CellStack>
    <CodeText code={code} />
    {hasMeaningfulDesc(code, description) ? <CellLine muted ellipsis>{description}</CellLine> : null}
  </CellStack>
)

// Shared search-field styling — mirrors the app's real filter inputs (FilterBar.js
// `inputOverrides`): boxy 6px radius, #ccc border, 33px height, 12px text applied to the
// INPUT + placeholder specifically (setting fontSize on the root alone doesn't reach the
// placeholder, which is what left it rendering at MUI's default 16px and overflowing).
export const searchFieldSx = {
  // minWidth (not width) so the module's `.rvpqc .row > *{width:auto}` Bootstrap-collision
  // override — which only sets `width` and out-specificity's an sx `width` — can't collapse it.
  minWidth: 300, maxWidth: 340, flex: '0 0 auto',
  '& .MuiOutlinedInput-root': { fontSize: 12, backgroundColor: '#fff', borderRadius: '6px', minHeight: 36, height: 36 },
  '& .MuiOutlinedInput-input': { padding: '8px 10px', fontSize: 12, color: '#333', height: 'auto' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
  '& input::placeholder, & .MuiInputBase-input::placeholder': { opacity: 1, color: '#999', fontSize: 12 },
}

export const SearchAdornment = { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }

// Subtle tinted, stronger-bordered column header so the grid separates from the white card it sits
// in — a plain white-on-white header read as "blended". Pass as `sx` to ProzoDataGrid (it forwards
// unknown props straight to the underlying DataGrid).
export const gridSx = {
  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' },
  '& .MuiDataGrid-columnHeaderTitle': { color: '#475569', fontWeight: 700 },
  '& .MuiDataGrid-row': { borderBottom: '1px solid #EDF1F5' },
  // Rows are auto-height; more vertical cell padding gives them breathing room so the table
  // doesn't read as congested.
  '& .MuiDataGrid-cell': { paddingTop: '12px', paddingBottom: '12px' },
  '& .MuiDataGrid-columnHeader': { paddingTop: '4px', paddingBottom: '4px' },
}

// Clean, neutral dropdown styling — mirrors the app's filter selects on Forward Shipments
// (white background, subtle grey border, boxy 6px radius, 12.5px text, quiet hover/focus).
// Replaces this module's earlier heavy blue-bordered / blue-tinted selects, which read as loud
// and off-standard next to the rest of the admin panel. Spread onto any MUI <Select sx={...}>.
export const selectSx = {
  fontSize: 12.5, backgroundColor: '#fff', borderRadius: '6px',
  '& .MuiSelect-select': { padding: '7px 30px 7px 11px', fontSize: 12.5, minHeight: 'unset', display: 'flex', alignItems: 'center' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#919EAB' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2065D1', borderWidth: '1px' },
  '&.Mui-disabled': { backgroundColor: '#F8FAFC' },
  '& .MuiSvgIcon-root': { color: 'text.secondary' },
}

// Matching dropdown menu — soft-shadowed rounded paper, 12.5px items — so the open list reads as
// clean as the closed control. Pass as MenuProps to the same <Select>.
export const selectMenuProps = {
  PaperProps: { sx: {
    maxHeight: 320, mt: 0.5, borderRadius: '8px', border: '1px solid #E2E8F0', boxShadow: '0 6px 24px rgba(15,23,42,.12)',
    '& .MuiMenuItem-root': { fontSize: 12.5, py: 0.75, minHeight: 'unset' },
    '& .MuiMenuItem-root.Mui-selected': { backgroundColor: '#EAF2FC' },
  } },
}

// ── Searchable dropdown (SearchSelect) ────────────────────────────────────────────────────────
// One reusable picker for the whole module so every searchable dropdown looks identical to the
// merchant picker: a search box, then a rounded portaled list with row separators, blue hover and
// two-line options. Built on MUI Autocomplete so the list can't be clipped by an accordion/grid
// the way a raw absolutely-positioned list would be.
export const pickerPaperSx = {
  borderRadius: '10px', mt: 0.5, border: '1px solid #E5E7EB', boxShadow: '0 8px 24px rgba(0,0,0,.12)',
  '& .MuiAutocomplete-listbox': { py: 0 },
  '& .MuiAutocomplete-option': { borderBottom: '1px solid #F3F4F6', px: 1.5, py: 1 },
  '& .MuiAutocomplete-option:last-of-type': { borderBottom: 'none' },
  '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused': { bgcolor: '#EAF2FC' },
}
export const pickerInputSx = {
  // A crisp border + small lift so the white field stands out even on a tinted background.
  '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '8px', fontSize: 12.5, py: '2px', boxShadow: '0 1px 2px rgba(16,24,40,.06)' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
  '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: '#94A3B8' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2065D1', borderWidth: '1px' },
  '& .Mui-focused.MuiOutlinedInput-root': { boxShadow: '0 0 0 3px rgba(32,101,209,.12)' },
  '& input::placeholder': { color: '#9ca3af', opacity: 1, fontSize: 12.5 },
}

// Props: options, value (option object or null), onChange(option|null), getOptionLabel,
// getOptionKey, getOptionDescription (optional), isOptionEqualToValue, filterText(option)->string,
// disabled, placeholder, resetOnSelect (for "add" pickers that shouldn't keep a selection),
// showSearchIcon, disableClearable, sx.
export function SearchSelect({
  options, value = null, onChange, getOptionLabel, getOptionKey, getOptionDescription,
  isOptionEqualToValue, filterText, disabled, placeholder, resetOnSelect = false,
  showSearchIcon = true, disableClearable = false, noOptionsText = 'No matches', sx,
}) {
  const keyOf = getOptionKey || getOptionLabel
  return (
    <Autocomplete
      options={options}
      value={value}
      disabled={disabled}
      size="small"
      blurOnSelect
      clearOnBlur={resetOnSelect}
      handleHomeEndKeys
      disableClearable={disableClearable}
      onChange={(e, opt) => onChange(opt)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(opts, { inputValue }) => {
        const t = inputValue.trim().toLowerCase()
        if (!t) return opts
        return opts.filter((o) => (filterText ? filterText(o) : getOptionLabel(o)).toLowerCase().includes(t))
      }}
      noOptionsText={noOptionsText}
      sx={sx}
      componentsProps={{ paper: { sx: pickerPaperSx } }}
      renderOption={(props, o) => {
        const desc = getOptionDescription ? getOptionDescription(o) : ''
        const { key, ...liProps } = props
        return (
          <Box component="li" {...liProps} key={keyOf(o)} title={typeof keyOf(o) === 'string' ? keyOf(o) : undefined}
            sx={{ flexDirection: 'column', alignItems: 'flex-start !important', gap: 0.25 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.primary', lineHeight: 1.35 }}>{getOptionLabel(o)}</Typography>
            {desc ? <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.35 }}>{desc}</Typography> : null}
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} sx={pickerInputSx}
          InputProps={{ ...params.InputProps, ...(showSearchIcon ? { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> } : {}) }} />
      )}
    />
  )
}

// Every table in this module already sits inside a `.card` (rvpQc.css) that provides its own
// white background + border + shadow — wrapping the grid in ANOTHER bg/shadow box on top of
// that (as Forward Shipments does, which has NO enclosing card) doubled up the chrome and made
// the outer card's already-subtle 1px border read as "no border at all". Since we're nested,
// just pass ProzoDataGrid `flat={false}` for a real, visible border around the grid itself —
// no separate wrapper needed.

// Pill stepper — cloned from the supply-side wizard's WizardStepper
// (src/app/pages/supply-side/components/WizardStepper.js): equal-width segmented pills that
// FILL the row (flex: '1 1 0'), each with a "Step N/total" eyebrow + label + numbered/checked
// badge, inside a bordered card. This is the app's own reusable pattern and handles a few
// long-labelled steps cleanly (each takes an even share of the width). RVP-QC allows free
// navigation, so every pill is clickable (no locked state). `current` is the 1-based step number;
// `steps` are { n, t }.
export function PillStepper({ steps, current, onChange }) {
  const total = steps.length
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)', p: { xs: 1.25, md: 1.5 }, mb: 2.5 }}>
      <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
        {steps.map((s) => {
          const done = s.n < current
          const isCur = s.n === current
          return (
            <Box
              key={s.n}
              title={s.t}
              onClick={() => onChange(s.n)}
              sx={{
                flex: '1 1 0', minWidth: 118, display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 1, borderRadius: 2.5, cursor: 'pointer', userSelect: 'none', transition: 'all .2s',
                bgcolor: isCur ? 'primary.main' : done ? (th) => alpha(th.palette.primary.main, 0.1) : 'action.hover',
                color: isCur ? '#fff' : done ? 'primary.dark' : 'text.secondary',
                boxShadow: isCur ? (th) => `0 6px 16px ${alpha(th.palette.primary.main, 0.35)}` : 'none',
                '&:hover': isCur ? {} : { bgcolor: done ? (th) => alpha(th.palette.primary.main, 0.17) : 'action.selected' },
              }}
            >
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11.5, fontWeight: 800,
                bgcolor: isCur ? 'rgba(255,255,255,.25)' : done ? 'primary.main' : 'background.paper',
                color: isCur ? '#fff' : done ? '#fff' : 'text.secondary',
                border: !isCur && !done ? '2px solid' : 'none', borderColor: 'divider',
              }}>
                {done ? <CheckIcon sx={{ fontSize: 14 }} /> : s.n}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', opacity: isCur ? 0.85 : 0.7, lineHeight: 1 }}>
                  Step {s.n}/{total}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12.5, fontWeight: isCur ? 700 : 600, lineHeight: 1.3, mt: 0.25 }}>
                  {s.t}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
